/**
 * Voice AI — Shared Guest Styles
 * Common CSS for guest sub-components (grid, detail, profile, conversations, memory tabs).
 */

import { css } from 'lit';

export const guestStyles = css`
  :host {
    display: block;
  }

  /* ─── Page Header ─── */
  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-8);
  }

  .page-header-text {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  .page-title {
    font-family: var(--font-display);
    font-size: var(--text-4xl);
    font-weight: var(--font-black);
    color: var(--color-text-primary);
    letter-spacing: -0.04em;
  }

  .page-description {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  /* ─── Search & Filters ─── */
  .search-filters {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    margin-bottom: var(--space-6);
    flex-wrap: wrap;
  }

  .results-count {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
    margin-left: auto;
  }

  /* ─── Guest Card Grid ─── */
  .guests-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-6);
    animation: slideUp var(--duration-slow) var(--ease-out) both;
  }

  .guest-card {
    background: rgba(255, 255, 255, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: var(--radius-xl);
    padding: var(--space-6);
    cursor: pointer;
    transition: all var(--transition-base);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
    position: relative;
    backdrop-filter: blur(8px) saturate(1.2);
    -webkit-backdrop-filter: blur(8px) saturate(1.2);
    overflow: hidden;
  }

  .guest-card:hover {
    box-shadow: var(--shadow-hover-lift);
    border-color: var(--color-border-secondary);
    transform: scale(1.01);
  }

  .guest-card-header {
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    margin-bottom: var(--space-4);
  }

  .guest-avatar {
    width: 44px;
    height: 44px;
    border-radius: var(--radius-full);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-base);
    font-weight: var(--font-bold);
    color: #fff;
    flex-shrink: 0;
    background: var(--color-accent);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
  }

  .guest-info {
    flex: 1;
    min-width: 0;
  }

  .guest-name {
    font-family: var(--font-display);
    font-size: var(--text-base);
    font-weight: var(--font-bold);
    color: var(--color-text-primary);
    letter-spacing: var(--tracking-tight);
    line-height: var(--leading-tight);
    margin-bottom: 2px;
  }

  .guest-phone {
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
  }

  .guest-stats-row {
    display: flex;
    gap: var(--space-4);
    margin-bottom: var(--space-3);
  }

  .guest-stat-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
  }

  .stat-value {
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-text-primary);
  }

  .stat-label {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
    letter-spacing: var(--tracking-wide);
  }

  .guest-card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: var(--space-3);
    border-top: 1px solid var(--color-border-subtle);
  }

  .guest-card-footer span {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
  }

  .guest-tags {
    display: flex;
    gap: var(--space-1);
    flex-wrap: wrap;
  }

  .guest-tag {
    padding: 1px 6px;
    border-radius: var(--radius-full);
    font-size: 10px;
    font-weight: var(--font-medium);
    background: rgba(99, 102, 241, 0.1);
    color: #6366f1;
  }

  /* ─── Detail Panel ─── */
  .detail-panel {
    animation: slideUp var(--duration-slow) var(--ease-out);
  }

  .detail-back-btn {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    background: transparent;
    border: none;
    border-radius: var(--radius-md);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: all var(--transition-fast);
    margin-bottom: var(--space-4);
  }

  .detail-back-btn:hover {
    color: var(--color-primary);
    background: var(--color-primary-light);
  }

  .detail-header {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    margin-bottom: var(--space-6);
  }

  .detail-avatar {
    width: 56px;
    height: 56px;
    border-radius: var(--radius-full);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-xl);
    font-weight: var(--font-bold);
    color: #fff;
    background: var(--color-accent);
    flex-shrink: 0;
  }

  .detail-title-area {
    flex: 1;
  }

  .detail-title {
    font-family: var(--font-display);
    font-size: var(--text-2xl);
    font-weight: var(--font-bold);
    color: var(--color-text-primary);
    letter-spacing: var(--tracking-tight);
  }

  .detail-subtitle {
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
    margin-top: 2px;
  }

  /* ─── Tab Bar ─── */
  .detail-tabs {
    display: flex;
    gap: var(--space-1);
    border-bottom: 1px solid var(--color-border-primary);
    margin-bottom: var(--space-6);
  }

  .detail-tab {
    padding: var(--space-3) var(--space-4);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-text-tertiary);
    cursor: pointer;
    border: none;
    background: transparent;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    transition: all var(--transition-fast);
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  .detail-tab:hover {
    color: var(--color-text-secondary);
  }

  .detail-tab.active {
    color: var(--color-accent);
    border-bottom-color: var(--color-accent);
    background: var(--color-accent-light);
    border-radius: var(--radius-md) var(--radius-md) 0 0;
  }

  /* ─── Glass Card ─── */
  .glass-card {
    background: rgba(255, 255, 255, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: var(--radius-xl);
    padding: var(--space-6);
    margin-bottom: var(--space-6);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(8px) saturate(1.2);
    -webkit-backdrop-filter: blur(8px) saturate(1.2);
    position: relative;
    overflow: hidden;
  }

  .section-title {
    font-family: var(--font-display);
    font-size: var(--text-lg);
    font-weight: var(--font-bold);
    color: var(--color-text-primary);
    letter-spacing: var(--tracking-tight);
    margin-bottom: var(--space-1);
  }

  .section-desc {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
    margin-bottom: var(--space-5);
  }

  /* Form base styles inherited from componentStyles (design-system.js) */

  .actions-footer {
    display: flex;
    justify-content: flex-end;
    padding-top: var(--space-4);
    border-top: 1px solid var(--color-border-primary);
    margin-top: var(--space-4);
  }

  /* ─── Conversation List ─── */
  .conversation-item {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    border-bottom: 1px solid var(--color-border-subtle);
    cursor: pointer;
    transition: background var(--transition-fast);
    border-radius: var(--radius-md);
  }

  .conversation-item:hover {
    background: rgba(99, 102, 241, 0.04);
  }

  .conversation-item:last-child {
    border-bottom: none;
  }

  .conv-icon {
    width: 32px;
    height: 32px;
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-xs);
    flex-shrink: 0;
    background: rgba(99, 102, 241, 0.1);
    color: #6366f1;
  }

  .conv-info {
    flex: 1;
    min-width: 0;
  }

  .conv-summary {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .conv-meta {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
    display: flex;
    gap: var(--space-2);
    margin-top: 2px;
  }

  .conv-outcome {
    padding: 1px 6px;
    border-radius: var(--radius-full);
    font-size: 10px;
    font-weight: var(--font-medium);
    background: rgba(16, 185, 129, 0.1);
    color: #10b981;
  }

  .conv-duration {
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
    flex-shrink: 0;
  }

  /* ─── Transcript Viewer ─── */
  .transcript-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    max-height: 400px;
    overflow-y: auto;
    padding: var(--space-3);
  }

  .transcript-msg {
    display: flex;
    flex-direction: column;
    max-width: 75%;
  }

  .transcript-msg.user { align-self: flex-start; }
  .transcript-msg.assistant { align-self: flex-end; }

  .transcript-bubble {
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-lg);
    font-size: var(--text-sm);
    line-height: 1.5;
    color: var(--color-text-primary);
  }

  .transcript-msg.user .transcript-bubble {
    background: var(--color-surface-primary, rgba(255,255,255,0.6));
    border: 1px solid var(--color-border-primary);
    border-bottom-left-radius: var(--radius-sm);
  }

  .transcript-msg.assistant .transcript-bubble {
    background: rgba(99, 102, 241, 0.08);
    border: 1px solid rgba(99, 102, 241, 0.15);
    border-bottom-right-radius: var(--radius-sm);
  }

  .transcript-speaker {
    font-size: 10px;
    font-weight: var(--font-semibold);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-text-tertiary);
    margin-bottom: 2px;
    padding: 0 var(--space-1);
  }

  .transcript-msg.assistant .transcript-speaker {
    text-align: right;
  }

  /* ─── Memory Entries ─── */
  .memory-category {
    margin-bottom: var(--space-5);
  }

  .memory-category-title {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-text-tertiary);
    margin-bottom: var(--space-2);
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  .memory-entry {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-md);
    transition: background var(--transition-fast);
  }

  .memory-entry:hover {
    background: rgba(99, 102, 241, 0.04);
  }

  .memory-key {
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
    min-width: 120px;
    flex-shrink: 0;
  }

  .memory-value {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-primary);
    flex: 1;
  }

  .memory-source {
    font-size: 10px;
    padding: 1px 5px;
    border-radius: var(--radius-full);
    background: var(--color-bg-tertiary);
    color: var(--color-text-tertiary);
    flex-shrink: 0;
  }

  .memory-actions {
    display: flex;
    gap: var(--space-1);
    opacity: 0;
    transition: opacity var(--transition-fast);
    flex-shrink: 0;
  }

  .memory-entry:hover .memory-actions {
    opacity: 1;
  }

  .memory-action-btn {
    width: 24px;
    height: 24px;
    border: none;
    background: transparent;
    border-radius: var(--radius-sm);
    color: var(--color-text-tertiary);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    transition: all var(--transition-fast);
  }

  .memory-action-btn:hover {
    color: var(--color-text-primary);
    background: var(--color-bg-tertiary);
  }

  .memory-action-btn.delete:hover {
    color: #ef4444;
    background: rgba(239, 68, 68, 0.1);
  }

  /* ─── Pagination ─── */
  .pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: var(--space-4);
    border-top: 1px solid var(--color-border-primary);
    margin-top: var(--space-4);
  }

  .pagination-info {
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
  }

  .pagination-controls {
    display: flex;
    align-items: center;
    gap: var(--space-1);
  }

  .page-btn {
    padding: var(--space-1) var(--space-2);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    background: var(--color-surface-primary, rgba(255,255,255,0.6));
    color: var(--color-text-secondary);
    font-size: var(--text-xs);
    cursor: pointer;
    min-width: 28px;
    text-align: center;
    transition: all var(--transition-fast);
  }

  .page-btn:hover:not(:disabled) {
    background: var(--color-surface-hover, rgba(99,102,241,0.04));
    color: var(--color-text-primary);
  }

  .page-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* ─── Loading & Empty States ─── */
  .skeleton-card {
    background: rgba(255, 255, 255, 0.18);
    padding: var(--space-6);
    border-radius: var(--radius-xl);
    backdrop-filter: blur(8px) saturate(1.2);
    -webkit-backdrop-filter: blur(8px) saturate(1.2);
  }

  .loading-spinner {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-10);
    color: var(--color-text-tertiary);
    font-size: var(--text-sm);
    gap: var(--space-2);
  }

  .loading-spinner i {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* ─── Toast ─── */
  .toast {
    position: fixed;
    bottom: var(--space-6);
    right: var(--space-6);
    background: var(--color-success);
    color: var(--color-text-inverse);
    padding: var(--space-3) var(--space-5);
    border-radius: var(--radius-lg);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    display: flex;
    align-items: center;
    gap: var(--space-2);
    box-shadow: var(--shadow-xl);
    z-index: var(--z-toast);
    transform: translateY(100px);
    opacity: 0;
    transition: all var(--duration-slow) var(--ease-out);
  }

  .toast.visible {
    transform: translateY(0);
    opacity: 1;
  }

  .toast.error {
    background: var(--color-error);
  }

  /* ─── Responsive ─── */
  @media (max-width: 1200px) {
    .guests-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 768px) {
    .guests-grid {
      grid-template-columns: 1fr;
    }
    .page-header {
      flex-wrap: wrap;
      gap: var(--space-3);
    }
  }
`;
